'use client';
import React, { useState, useEffect, useCallback, useRef } from 'react';
import Link from 'next/link';
import { PlusCircle, Download, Sparkles, Copy } from 'lucide-react';
import { type ClaimItem } from '@/lib/claimStore';
import { fetchClaimItems, deleteClaimItem, updateClaimItem } from '@/lib/claimService';
import { getChatCompletion } from '@/lib/ai/chatCompletion';
import { useClaimContext } from '@/contexts/ClaimContext';
import { createClient } from '@/lib/supabase/client';
import KpiCards from './KpiCards';
import InventoryFilters from './InventoryFilters';
import InventoryTable from './InventoryTable';
import ConfirmModal from '@/components/ui/ConfirmModal';
import { toast } from 'sonner';

export default function ClaimInventoryContent() {
  const { activeClaim, refreshClaims } = useClaimContext();
  const [items, setItems] = useState<ClaimItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [activeRoom, setActiveRoom] = useState('all');
  const [activeMember, setActiveMember] = useState('all');
  const [activeStatus, setActiveStatus] = useState('all');
  const [activeCategory, setActiveCategory] = useState('all');
  const [isResearchingAll, setIsResearchingAll] = useState(false);
  const [researchProgress, setResearchProgress] = useState<{ current: number; total: number } | null>(null);
  const [removeDupOpen, setRemoveDupOpen] = useState(false);
  const [dupCount, setDupCount] = useState(0);

  // Set of item IDs that were written by this client — used to suppress realtime echoes
  const selfWrittenIds = useRef<Set<string>>(new Set());
  // Debounce timer for realtime-triggered reloads
  const reloadTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Whether a local write is in-flight (block realtime reloads during writes)
  const localWriteInFlight = useRef(false);
  // Active claim id ref for subscription closure
  const activeClaimIdRef = useRef<string | null>(null);

  const loadItems = useCallback(async (claimId?: string | null) => {
    const id = claimId !== undefined ? claimId : activeClaimIdRef.current;
    setLoading(true);
    const data = await fetchClaimItems(id ?? undefined);
    // Deduplicate by id as a final safety net
    const seen = new Set<string>();
    const deduped = data.filter((item) => {
      if (seen.has(item.id)) return false;
      seen.add(item.id);
      return true;
    });
    setItems(deduped);
    setLoading(false);
  }, []);

  // Keep activeClaimIdRef in sync and reload when claim changes
  useEffect(() => {
    activeClaimIdRef.current = activeClaim?.id ?? null;
    loadItems(activeClaim?.id ?? null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeClaim?.id]);

  // Refresh unpriced count in sidebar whenever items change
  useEffect(() => {
    refreshClaims();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [items]);

  // ── Local realtime subscription ──────────────────────────────────────────
  useEffect(() => {
    const supabase = createClient();

    const channel = supabase
      .channel('inventory-content-rt')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'ct_claim_items' },
        (payload) => {
          // Ignore echoes of our own writes
          const changedId: string | undefined =
            (payload.new as { id?: string } | null)?.id ??
            (payload.old as { id?: string } | null)?.id;

          if (changedId && selfWrittenIds.current.has(changedId)) {
            selfWrittenIds.current.delete(changedId);
            return;
          }

          // If a local write is in-flight, skip the reload entirely
          if (localWriteInFlight.current) return;

          // Debounce: wait 600 ms before reloading to batch rapid events
          if (reloadTimer.current) clearTimeout(reloadTimer.current);
          reloadTimer.current = setTimeout(() => {
            loadItems();
          }, 600);
        }
      )
      .subscribe();

    return () => {
      if (reloadTimer.current) clearTimeout(reloadTimer.current);
      supabase.removeChannel(channel);
    };
  }, [loadItems]);
  // ─────────────────────────────────────────────────────────────────────────

  const rooms = Array.from(new Set(items.map((i) => i.location))).sort();
  const members = Array.from(new Set(items.map((i) => i.addedBy))).sort();
  const categories = Array.from(new Set(items.map((i) => i.category).filter(Boolean))).sort() as string[];

  const filtered = items.filter((item) => {
    const matchSearch =
      search === '' ||
      item.name.toLowerCase().includes(search.toLowerCase()) ||
      item.description.toLowerCase().includes(search.toLowerCase()) ||
      item.notes.toLowerCase().includes(search.toLowerCase());
    const matchRoom = activeRoom === 'all' || item.location === activeRoom;
    const matchMember = activeMember === 'all' || item.addedBy === activeMember;
    const matchStatus = activeStatus === 'all' || item.priceStatus === activeStatus;
    const matchCategory = activeCategory === 'all' || item.category === activeCategory;
    return matchSearch && matchRoom && matchMember && matchStatus && matchCategory;
  });

  const handleDelete = async (id: string) => {
    selfWrittenIds.current.add(id);
    const ok = await deleteClaimItem(id);
    if (ok) {
      setItems((prev) => prev.filter((i) => i.id !== id));
    } else {
      selfWrittenIds.current.delete(id);
      toast.error('Failed to delete item');
    }
  };

  const handleResearchPrice = async (id: string) => {
    const item = items.find((i) => i.id === id);
    if (!item) return;

    localWriteInFlight.current = true;
    selfWrittenIds.current.add(id);
    setItems((prev) =>
      prev.map((i) => i.id === id ? { ...i, priceStatus: 'researching' as const } : i)
    );
    toast.info('Researching replacement price…', { duration: 2000 });

    const updates = await researchPriceForItem(item);
    if (updates) {
      const updated = await updateClaimItem(id, updates);
      if (updated) {
        setItems((prev) => prev.map((i) => i.id === id ? updated : i));
        toast.success('Price research complete');
      } else {
        selfWrittenIds.current.delete(id);
        setItems((prev) => prev.map((i) => i.id === id ? { ...i, priceStatus: 'unpriced' as const } : i));
        toast.error('Failed to save price research');
      }
    } else {
      selfWrittenIds.current.delete(id);
      setItems((prev) => prev.map((i) => i.id === id ? { ...i, priceStatus: 'unpriced' as const } : i));
      toast.error('Could not research price. Please try again.');
    }
    localWriteInFlight.current = false;
  };

  async function researchPriceForItem(item: ClaimItem): Promise<Partial<ClaimItem> | null> {
    try {
      const result = await getChatCompletion(
        'PERPLEXITY',
        'perplexity/sonar-pro',
        [
          {
            role: 'system',
            content:
              'You are a replacement cost estimator for insurance claims. Given an item name and description, find the current MSRP (Manufacturer\'s Suggested Retail Price) and one retail price from a reputable retailer that sells only brand-new products. Do NOT use eBay, Amazon, Craigslist, Facebook Marketplace, OfferUp, or any marketplace that sells used, refurbished, or reconditioned items. Use the retail price (not MSRP) as the primary "price" value. Respond ONLY with a JSON object in this exact format: {"price": <retail price as number>, "msrp": <msrp as number or null>, "sourceName": "<retailer name>", "sourceUrl": "<url>", "confidence": "high"|"medium"|"low", "notes": "<brief explanation including both MSRP and retail price>"}. Do not include any text outside the JSON.',
          },
          {
            role: 'user',
            content: `Item: ${item.name}${item.description ? `\nDescription: ${item.description}` : ''}${item.location ? `\nLocation: ${item.location}` : ''}\n\nProvide the MSRP and one retail price (new only) from a reputable retailer. Do not use eBay, Amazon, or any used/reconditioned marketplace.`,
          },
        ],
        { temperature: 0.2, max_tokens: 300 }
      );

      const content: string = result?.choices?.[0]?.message?.content ?? '';
      const jsonMatch = content.match(/\{[\s\S]*\}/);
      if (!jsonMatch) return null;

      const parsed = JSON.parse(jsonMatch[0]);
      if (!parsed.price || typeof parsed.price !== 'number') return null;

      const today = new Date().toISOString().split('T')[0];

      const searchResults: Array<{ url: string; title: string }> = result?.search_results ?? [];
      const sources = searchResults.slice(0, 5).map((s: { url: string; title: string }) => ({
        name: s.title || s.url,
        url: s.url,
      }));

      return {
        priceStatus: 'priced' as const,
        unitPrice: parsed.price,
        priceResearch: {
          price: parsed.price,
          sourceName: parsed.sourceName ?? 'Online Retailer',
          sourceUrl: parsed.sourceUrl ?? '',
          researchDate: today,
          confidence: parsed.confidence ?? 'medium',
          notes: parsed.notes ?? '',
          sources: sources.length > 0 ? sources : undefined,
        },
      };
    } catch {
      return null;
    }
  }

  const handleResearchAllPrices = async () => {
    const unpricedItems = items.filter(
      (i) => i.priceStatus === 'unpriced' || i.priceStatus === 'researching'
    );

    if (unpricedItems.length === 0) {
      toast.info('All items already have prices researched.');
      return;
    }

    localWriteInFlight.current = true;
    setIsResearchingAll(true);
    setResearchProgress({ current: 0, total: unpricedItems.length });

    // Register all IDs as self-written
    unpricedItems.forEach((i) => selfWrittenIds.current.add(i.id));

    setItems((prev) =>
      prev.map((i) =>
        unpricedItems.some((u) => u.id === i.id)
          ? { ...i, priceStatus: 'researching' as const }
          : i
      )
    );

    let successCount = 0;
    let failCount = 0;

    for (let idx = 0; idx < unpricedItems.length; idx++) {
      const item = unpricedItems[idx];
      setResearchProgress({ current: idx + 1, total: unpricedItems.length });

      const updates = await researchPriceForItem(item);

      if (updates) {
        const updated = await updateClaimItem(item.id, updates);
        if (updated) {
          setItems((prev) => prev.map((i) => i.id === item.id ? updated : i));
          successCount++;
        } else {
          selfWrittenIds.current.delete(item.id);
          setItems((prev) =>
            prev.map((i) => i.id === item.id ? { ...i, priceStatus: 'unpriced' as const } : i)
          );
          failCount++;
        }
      } else {
        selfWrittenIds.current.delete(item.id);
        setItems((prev) =>
          prev.map((i) => i.id === item.id ? { ...i, priceStatus: 'unpriced' as const } : i)
        );
        failCount++;
      }
    }

    setIsResearchingAll(false);
    setResearchProgress(null);
    localWriteInFlight.current = false;

    if (successCount > 0 && failCount === 0) {
      toast.success(`Researched prices for ${successCount} item${successCount !== 1 ? 's' : ''}.`);
    } else if (successCount > 0 && failCount > 0) {
      toast.success(`Researched ${successCount} item${successCount !== 1 ? 's' : ''}. ${failCount} could not be priced.`);
    } else {
      toast.error('Could not research prices. Please try again.');
    }
  };

  const handleBulkResearchPrices = async (ids: string[]) => {
    const targetItems = items.filter((i) => ids.includes(i.id));
    if (targetItems.length === 0) return;

    localWriteInFlight.current = true;
    targetItems.forEach((i) => selfWrittenIds.current.add(i.id));

    setItems((prev) =>
      prev.map((i) =>
        ids.includes(i.id) ? { ...i, priceStatus: 'researching' as const } : i
      )
    );

    toast.info(
      `Researching prices for ${targetItems.length} item${targetItems.length !== 1 ? 's' : ''}…`,
      { duration: 3000 }
    );

    let successCount = 0;
    let failCount = 0;

    for (const item of targetItems) {
      const updates = await researchPriceForItem(item);
      if (updates) {
        const updated = await updateClaimItem(item.id, updates);
        if (updated) {
          setItems((prev) => prev.map((i) => i.id === item.id ? updated : i));
          successCount++;
        } else {
          selfWrittenIds.current.delete(item.id);
          setItems((prev) =>
            prev.map((i) => i.id === item.id ? { ...i, priceStatus: 'unpriced' as const } : i)
          );
          failCount++;
        }
      } else {
        selfWrittenIds.current.delete(item.id);
        setItems((prev) =>
          prev.map((i) => i.id === item.id ? { ...i, priceStatus: 'unpriced' as const } : i)
        );
        failCount++;
      }
    }

    localWriteInFlight.current = false;

    if (successCount > 0 && failCount === 0) {
      toast.success(`Researched prices for ${successCount} item${successCount !== 1 ? 's' : ''}.`);
    } else if (successCount > 0 && failCount > 0) {
      toast.success(`Researched ${successCount} item${successCount !== 1 ? 's' : ''}. ${failCount} could not be priced.`);
    } else {
      toast.error('Could not research prices for selected items. Please try again.');
    }
  };

  /** Find duplicate IDs to remove (keep first occurrence per name+location key) */
  const getDuplicateIds = useCallback((): string[] => {
    const seen = new Map<string, string>();
    const toRemove: string[] = [];
    for (const item of items) {
      const key = `${item.name.trim().toLowerCase()}||${item.location.trim().toLowerCase()}`;
      if (seen.has(key)) {
        toRemove.push(item.id);
      } else {
        seen.set(key, item.id);
      }
    }
    return toRemove;
  }, [items]);

  const handleRemoveDuplicatesClick = () => {
    const ids = getDuplicateIds();
    if (ids.length === 0) {
      toast.info('No duplicate items found.');
      return;
    }
    setDupCount(ids.length);
    setRemoveDupOpen(true);
  };

  const handleConfirmRemoveDuplicates = async () => {
    const ids = getDuplicateIds();
    setRemoveDupOpen(false);
    ids.forEach((id) => selfWrittenIds.current.add(id));
    let removed = 0;
    for (const id of ids) {
      const ok = await deleteClaimItem(id);
      if (ok) {
        setItems((prev) => prev.filter((i) => i.id !== id));
        removed++;
      } else {
        selfWrittenIds.current.delete(id);
      }
    }
    if (removed > 0) {
      toast.success(`Removed ${removed} duplicate item${removed !== 1 ? 's' : ''}.`);
    } else {
      toast.error('Could not remove duplicates. Please try again.');
    }
  };

  const unpricedCount = items.filter(
    (i) => i.priceStatus === 'unpriced' || i.priceStatus === 'researching'
  ).length;

  const handleUpdate = async (id: string, updates: Partial<ClaimItem>) => {
    localWriteInFlight.current = true;
    selfWrittenIds.current.add(id);
    const updated = await updateClaimItem(id, updates);
    if (updated) {
      setItems((prev) => prev.map((i) => i.id === id ? updated : i));
      toast.success('Item updated');
    } else {
      selfWrittenIds.current.delete(id);
      toast.error('Failed to update item');
    }
    localWriteInFlight.current = false;
  };

  const handleBulkUpdate = async (ids: string[], updates: Partial<ClaimItem>) => {
    localWriteInFlight.current = true;
    ids.forEach((id) => selfWrittenIds.current.add(id));
    const results = await Promise.all(ids.map((id) => updateClaimItem(id, updates)));
    let successCount = results.filter(Boolean).length;
    let failCount = ids.length - successCount;
    results.forEach((updated, i) => {
      if (updated) {
        setItems((prev) => prev.map((item) => item.id === ids[i] ? updated : item));
      } else {
        selfWrittenIds.current.delete(ids[i]);
      }
    });
    localWriteInFlight.current = false;
    if (failCount === 0) {
      toast.success(`Updated ${successCount} item${successCount !== 1 ? 's' : ''}`);
    } else {
      toast.success(`Updated ${successCount} item${successCount !== 1 ? 's' : ''}. ${failCount} failed.`);
    }
  };

  return (
    <div className="space-y-6 fade-in">
      {/* Page header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-foreground">Claim Inventory</h1>
          <p className="text-sm text-muted-foreground mt-1">
            {activeClaim ? activeClaim.name : 'Fire loss documentation'}
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          {unpricedCount > 0 && (
            <button
              type="button"
              onClick={handleResearchAllPrices}
              disabled={isResearchingAll}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-primary/40 rounded-lg bg-primary/10 text-primary hover:bg-primary/20 transition-all duration-150 active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed disabled:active:scale-100"
            >
              {isResearchingAll ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                  {researchProgress
                    ? `Researching ${researchProgress.current}/${researchProgress.total}…`
                    : 'Researching…'}
                </>
              ) : (
                <>
                  <Sparkles size={15} />
                  Research All Prices
                  <span className="ml-0.5 inline-flex items-center justify-center w-5 h-5 rounded-full bg-primary text-primary-foreground text-xs font-bold">
                    {unpricedCount}
                  </span>
                </>
              )}
            </button>
          )}
          <button
            type="button"
            onClick={handleRemoveDuplicatesClick}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border rounded-lg bg-card hover:bg-secondary transition-all duration-150 active:scale-95"
          >
            <Copy size={15} /> Remove Duplicates
          </button>
          <Link
            href="/export-claim-summary"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium border border-border rounded-lg bg-card hover:bg-secondary transition-all duration-150 active:scale-95"
          >
            <Download size={15} /> Export
          </Link>
          <Link
            href="/item-entry"
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:bg-amber-700 transition-all duration-150 active:scale-95"
          >
            <PlusCircle size={15} /> Add Item
          </Link>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          <span className="ml-3 text-sm text-muted-foreground">Loading inventory…</span>
        </div>
      ) : (
        <>
          {/* KPI cards */}
          <KpiCards items={items} />

          {/* Filters */}
          <div className="bg-card border border-border rounded-xl p-4">
            <InventoryFilters
              search={search}
              onSearchChange={setSearch}
              activeRoom={activeRoom}
              onRoomChange={setActiveRoom}
              activeMember={activeMember}
              onMemberChange={setActiveMember}
              activeStatus={activeStatus}
              onStatusChange={setActiveStatus}
              activeCategory={activeCategory}
              onCategoryChange={setActiveCategory}
              rooms={rooms}
              members={members}
              categories={categories}
            />
          </div>

          {/* Table */}
          <InventoryTable
            items={filtered}
            rooms={rooms}
            members={members}
            onDelete={handleDelete}
            onResearchPrice={handleResearchPrice}
            onUpdate={handleUpdate}
            onBulkUpdate={handleBulkUpdate}
            onBulkResearchPrices={handleBulkResearchPrices}
            totalCount={filtered.length}
          />
        </>
      )}

      <ConfirmModal
        open={removeDupOpen}
        title={`Remove ${dupCount} duplicate item${dupCount !== 1 ? 's' : ''}?`}
        description={`${dupCount} item${dupCount !== 1 ? 's' : ''} with the same name and room as an existing item will be permanently deleted. The first occurrence of each duplicate will be kept. This cannot be undone.`}
        confirmLabel={`Remove ${dupCount} Duplicate${dupCount !== 1 ? 's' : ''}`}
        onConfirm={handleConfirmRemoveDuplicates}
        onCancel={() => setRemoveDupOpen(false)}
      />
    </div>
  );
}